/** QuickPlan — Géométrie dérivée côté client (mémorisée par version du projet). */
import { resolveComponent, unionBBox } from '../shared/resolve.js';
import { computeFrames, bboxInFrame, portsInFrame } from '../shared/frames.js';

const resolvedCache = new WeakMap(); // composant → Map(clé params → résolu)

/** Résolution mémorisée (un composant + des paramètres donnent toujours la même chose). */
export function resolved(comp, params) {
  let m = resolvedCache.get(comp);
  if (!m) { m = new Map(); resolvedCache.set(comp, m); }
  const key = JSON.stringify(params ?? {});
  let r = m.get(key);
  if (!r) {
    try { r = resolveComponent(comp, params); } catch (e) { r = { error: e.message }; }
    if (m.size > 500) m.clear();
    m.set(key, r);
  }
  return r;
}

export function resolvedOf(inst, lookup) {
  const c = lookup(inst.componentId);
  const r = c && resolved(c, inst.params);
  return r && !r.error ? r : null;
}

let framesMemo = { project: null, lookup: null, value: null };

/** Repères monde de toutes les instances (hiérarchie et articulations comprises). */
export function frames(project, lookup) {
  if (framesMemo.project !== project || framesMemo.lookup !== lookup) {
    framesMemo = { project, lookup, value: computeFrames(project, (i) => resolvedOf(i, lookup)) };
  }
  return framesMemo.value;
}

let boundsMemo = { project: null, value: null };

/** Encombrement hors tout du projet. */
export function projectBounds(project, lookup) {
  if (boundsMemo.project === project) return boundsMemo.value;
  const F = frames(project, lookup);
  const boxes = [];
  for (const i of project?.instances ?? []) {
    const r = resolvedOf(i, lookup);
    if (r) boxes.push(bboxInFrame(F.get(i.id), r));
  }
  boundsMemo = { project, value: unionBBox(boxes) };
  return boundsMemo.value;
}

export function instanceBBox(inst, lookup, project) {
  const r = resolvedOf(inst, lookup);
  return r ? bboxInFrame(frames(project, lookup).get(inst.id), r) : null;
}

export function instancePorts(inst, lookup, project) {
  const r = resolvedOf(inst, lookup);
  return r ? portsInFrame(frames(project, lookup).get(inst.id), r) : [];
}

/** Instances visibles : ni masquées, ni portées par une pièce masquée, ni dans un
 *  sous-ensemble masqué. */
export function hiddenSet(project) {
  const hiddenGroups = new Set();
  const gById = new Map(project.groups.map((g) => [g.id, g]));
  for (const g of project.groups) {
    let cur = g;
    let guard = 0;
    while (cur && guard++ < 50) {
      if (cur.hidden) { hiddenGroups.add(g.id); break; }
      cur = cur.parentId ? gById.get(cur.parentId) : null;
    }
  }
  const byId = new Map(project.instances.map((i) => [i.id, i]));
  const out = new Set();
  for (const i of project.instances) {
    let cur = i;
    let guard = 0;
    while (cur && guard++ < 100) {
      if (cur.hidden || (cur.groupId && hiddenGroups.has(cur.groupId))) { out.add(i.id); break; }
      cur = cur.parentId ? byId.get(cur.parentId) : null;
    }
  }
  return out;
}

export const fmt = (v, d = 1) => (Math.round(v * 10 ** d) / 10 ** d).toLocaleString('fr-FR');
